import React from 'react';
import {
  CommonActions,
  NavigationProp,
  useNavigation,
  useRoute,
} from '@react-navigation/native';
import {
  SafeAreaView,
  ScrollView,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from 'react-native';

import { ms, s, vs } from '../../utils/responsive';
import { navigationRef } from '../../services/navigationService';

interface Props {
  navigation: NavigationProp<any>;
}

/**
 * Step 3 of 3 — terminal success screen. The code is already saved on
 * the backend by the time this screen renders. Two jobs:
 *
 *   1. Reinforce the "write it down now" message one more time.
 *   2. Reset the navigation stack back to the RecoveryCodeSettings hub
 *      so the user can't hit "back" to re-enter the input screen.
 */
const SetRecoveryCodeSuccessScreen: React.FC<Props> = ({ navigation }) => {
  const nav = useNavigation();
  const returnTo = (useRoute<any>().params as
    | { returnTo?: { root: string; routes: { name: string; params?: object }[] } }
    | undefined)?.returnTo;

  const handleDone = () => {
    // Sent here by a document save: go back there to save again.
    if (returnTo && returnTo.routes.length > 0 && navigationRef.isReady()) {
      navigationRef.dispatch(
        CommonActions.reset({
          index: 0,
          routes: [
            { name: returnTo.root, state: { index: returnTo.routes.length - 1, routes: returnTo.routes } },
          ],
        }),
      );
      return;
    }
    // Reset so back-stack doesn't contain Intro / Input after success.
    nav.dispatch(
      CommonActions.reset({
        index: 0,
        routes: [{ name: 'RecoveryCodeSettings' }],
      }),
    );
  };

  return (
    <SafeAreaView style={styles.container}>
      <ScrollView contentContainerStyle={styles.content}>
        <View style={styles.badge}>
          <Text style={styles.badgeEmoji}>✓</Text>
        </View>

        <Text style={styles.stepLabel}>Step 3 of 3</Text>
        <Text style={styles.title}>Recovery code saved</Text>

        <Text style={styles.description}>
          Your vault key is now encrypted with your recovery code. Sign in on a
          new device will ask you for this code.
        </Text>

        <View style={styles.warningBox}>
          <Text style={styles.warningTitle}>⚠ Write it down right now</Text>
          <Text style={styles.warningBody}>
            This is your one reminder. If you forget your recovery code,
            we cannot recover your vault. Save it in a password manager, on
            paper in a safe, or shared with a successor you trust —
            somewhere you'll find it a year from now.
          </Text>
        </View>

        <TouchableOpacity
          style={styles.primaryButton}
          onPress={handleDone}
          accessibilityRole="button"
        >
          <Text style={styles.primaryButtonText}>I've saved it — done</Text>
        </TouchableOpacity>
      </ScrollView>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F3F6F8',
  },
  content: {
    padding: s(20),
    alignItems: 'center',
    paddingTop: vs(40),
  },
  badge: {
    width: ms(72),
    height: ms(72),
    borderRadius: ms(36),
    backgroundColor: '#D4F5E0',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: vs(20),
  },
  badgeEmoji: {
    fontSize: ms(36),
    color: '#2E7D3E',
    fontWeight: '700',
  },
  stepLabel: {
    fontSize: ms(12),
    fontWeight: '600',
    color: '#29BFC0',
    textTransform: 'uppercase',
    letterSpacing: 1,
    marginBottom: vs(4),
  },
  title: {
    fontSize: ms(22),
    fontWeight: '700',
    color: '#1A1A1A',
    marginBottom: vs(12),
    textAlign: 'center',
  },
  description: {
    fontSize: ms(14),
    color: '#5A5A5A',
    lineHeight: ms(22),
    textAlign: 'center',
    marginBottom: vs(20),
  },
  warningBox: {
    backgroundColor: '#FFF4E0',
    borderWidth: 1,
    borderColor: '#E6A23C',
    borderRadius: ms(8),
    padding: ms(14),
    width: '100%',
    marginBottom: vs(28),
  },
  warningTitle: {
    color: '#92400E',
    fontWeight: '700',
    fontSize: ms(14),
    marginBottom: vs(6),
  },
  warningBody: {
    color: '#5A3F0F',
    fontSize: ms(13),
    lineHeight: ms(20),
  },
  primaryButton: {
    backgroundColor: '#29BFC0',
    paddingVertical: vs(14),
    paddingHorizontal: s(40),
    borderRadius: ms(8),
    minWidth: ms(240),
    alignItems: 'center',
  },
  primaryButtonText: {
    color: '#FFFFFF',
    fontSize: ms(16),
    fontWeight: '600',
  },
});

export default SetRecoveryCodeSuccessScreen;
